import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Badge } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import SideNavbar from './helpers/SideNavbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMessage, 
  faEdit, 
  faRss, 
  faMedal, 
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { 
  FaLinkedin, 
  FaTwitter,
  FaFacebook,
  FaInstagram
} from 'react-icons/fa';
import './PlatformSettings.css';

const PlatformSettings = () => {
  const UserID = localStorage.getItem('UserID');
  const AccountType = localStorage.getItem('AccountType');
  const [loading, setLoading] = useState(true);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [savingModule, setSavingModule] = useState(null);
  const [modules, setModules] = useState({
    schedulePost: {
      enabled: false,
      platforms: {}
    },
    knackMoments: {
      enabled: false,
      platforms: {}
    },
    knackFeeds: {
      enabled: false,
      platforms: {}
    },
    knackChallenges: {
      enabled: false,
      platforms: {}
    },
    createPost: {
      enabled: false,
      platforms: {}
    }
  });

  useEffect(() => {
    const initializeData = async () => {
      await fetchPlatforms();
      await fetchSettings();
    };
    initializeData();
  }, []);

  // Filter platforms based on AccountType - Facebook and Instagram only for Page accounts
  const filterPlatformsByAccountType = (platforms) => {
    if (!platforms || platforms.length === 0) return platforms;
    
    // If AccountType is not "Page", filter out Facebook and Instagram
    if (AccountType !== 'Page' && AccountType !== 'page') {
      return platforms.filter(platform => {
        const platformCode = platform.code || platform.name?.toLowerCase();
        return platformCode !== 'facebook' && platformCode !== 'instagram';
      });
    }
    
    return platforms;
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platforms`
      );
      
      if (response.data.success && response.data.platforms) {
        const allPlatforms = response.data.platforms;
        // Filter platforms based on AccountType
        const filteredPlatforms = filterPlatformsByAccountType(allPlatforms);
        setAvailablePlatforms(filteredPlatforms);
        
        // Initialize modules with filtered platforms enabled by default
        const defaultPlatforms = {};
        filteredPlatforms.forEach(platform => {
          const platformCode = platform.code || platform.name?.toLowerCase();
          defaultPlatforms[platformCode] = true;
        });
        
        setModules(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(moduleKey => {
            // Only initialize if platforms object is empty
            if (!updated[moduleKey].platforms || Object.keys(updated[moduleKey].platforms).length === 0) {
              updated[moduleKey] = {
                ...updated[moduleKey],
                platforms: { ...defaultPlatforms }
              };
            }
          });
          return updated;
        });
        
        console.log('✅ Platforms loaded:', filteredPlatforms);
        console.log('ℹ️ AccountType:', AccountType, '- Facebook & Instagram', AccountType === 'Page' || AccountType === 'page' ? 'included' : 'excluded');
        return filteredPlatforms;
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
      // Fallback to default platforms if API fails
      const fallbackPlatforms = [
        { id: 1, name: 'LinkedIn', code: 'linkedin' },
        { id: 3, name: 'Facebook', code: 'facebook' }
      ];
      const filteredFallback = filterPlatformsByAccountType(fallbackPlatforms);
      setAvailablePlatforms(filteredFallback);
      return filteredFallback;
    }
  };

  const fetchSettings = async () => {
    if (!UserID) {
      console.error('UserID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Fetch all platform settings for all modules
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/all/${UserID}`
      );
      
      if (response.data.success && response.data.settings && Array.isArray(response.data.settings)) {
        // Transform database settings to UI format
        const dbSettings = response.data.settings;
        
        // Wait for platforms to be loaded if not already
        let platforms = availablePlatforms;
        if (platforms.length === 0) {
          platforms = await fetchPlatforms() || [];
        }
        
        // Initialize default platforms object - all enabled by default
        const defaultPlatforms = {};
        platforms.forEach(platform => {
          const platformCode = platform.code || platform.name?.toLowerCase();
          defaultPlatforms[platformCode] = true;
        });
        
        setModules(prev => {
          const updated = { ...prev };
          
          // Process each module
          Object.keys(updated).forEach(moduleKey => {
            // Map module keys to database module names
            const moduleNameMap = {
              'schedulePost': 'schedule_post',
              'knackMoments': 'knack_moments',
              'knackFeeds': 'knack_feeds',
              'knackChallenges': 'knack_challenges',
              'createPost': 'create_post'
            };
            
            const dbModuleName = moduleNameMap[moduleKey] || moduleKey;
            
            const moduleSettings = dbSettings.filter(s => 
              s.module_name && s.module_name.toLowerCase() === dbModuleName.toLowerCase()
            );
            
            // Initialize platforms as enabled by default
            updated[moduleKey].platforms = { ...defaultPlatforms };
            
            if (moduleSettings.length > 0) {
              // Check if module is enabled (check first record's module_setting_status)
              const moduleEnabled = moduleSettings[0].module_setting_status === 1 || 
                                   moduleSettings[0].module_setting_status === true;
              updated[moduleKey].enabled = moduleEnabled;
              
              // For initial state, keep all platforms enabled by default
              // Only update platforms that are explicitly enabled in database
              // This ensures all platforms show as enabled initially
              moduleSettings.forEach(setting => {
                const platformName = setting.platform_name?.toLowerCase();
                if (platformName) {
                  // Skip Facebook and Instagram settings if AccountType is not Page
                  if ((platformName === 'facebook' || platformName === 'instagram') && AccountType !== 'Page' && AccountType !== 'page') {
                    return;
                  }
                  // Only update platforms that are in availablePlatforms
                  const platformExists = platforms.some(p => {
                    const platformCode = p.code || p.name?.toLowerCase();
                    return platformCode === platformName;
                  });
                  if (platformExists) {
                    // Keep platforms enabled by default (initial state)
                    // Database settings will be respected when user manually toggles
                    updated[moduleKey].platforms[platformName] = true;
                  }
                }
              });
            } else {
              // If no settings exist, ensure module is enabled by default
              updated[moduleKey].enabled = true;
            }
          });
          
          return updated;
        });
        
        console.log('✅ Platform settings loaded');
      } else {
        console.log('ℹ️ No settings found, using defaults');
      }
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  };

  const saveModuleSettings = async (moduleKey, moduleData) => {
    if (!UserID) {
      console.error('UserID not found');
      return;
    }

    setSavingModule(moduleKey);
    try {
      const moduleEnabled = moduleData.enabled;
      
      // Map module keys to database module names
      const moduleNameMap = {
        'schedulePost': 'schedule_post',
        'knackMoments': 'knack_moments',
        'knackFeeds': 'knack_feeds',
        'knackChallenges': 'knack_challenges',
        'createPost': 'create_post'
      };
      
      const dbModuleName = moduleNameMap[moduleKey] || moduleKey;
      
      // Prepare settings array for this module
      // Filter out Facebook and Instagram if AccountType is not Page
      const settingsToSave = Object.keys(moduleData.platforms)
        .filter(platformKey => {
          // Exclude Facebook and Instagram if AccountType is not Page
          const platformKeyLower = platformKey.toLowerCase();
          if ((platformKeyLower === 'facebook' || platformKeyLower === 'instagram') && AccountType !== 'Page' && AccountType !== 'page') {
            return false;
          }
          return true;
        })
        .map(platformKey => ({
          module_name: dbModuleName,
          platform_name: platformKey,
          platform_status: moduleData.platforms[platformKey],
          module_setting_status: moduleEnabled
        }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/bulk`,
        {
          UserID: parseInt(UserID),
          settings: settingsToSave
        }
      );
      
      if (response.data.success) {
        console.log(`✅ Settings saved for ${moduleKey}`);
        // Show subtle success notification
        Swal.fire({
          icon: 'success',
          title: 'Saved',
          timer: 1000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } else {
        throw new Error(response.data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error(`Error saving settings for ${moduleKey}:`, error);
      // Show a subtle notification instead of blocking alert
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: 'Failed to save settings. Please try again.',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } finally {
      setSavingModule(null);
    }
  };

  const toggleModule = (moduleKey) => {
    setModules(prev => {
      const newEnabled = !prev[moduleKey].enabled;
      const updatedModule = {
        ...prev[moduleKey],
        enabled: newEnabled,
        // When disabling the module, turn off all platforms
        platforms: newEnabled ? prev[moduleKey].platforms : Object.keys(prev[moduleKey].platforms).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {})
      };
      
      const updatedModules = {
        ...prev,
        [moduleKey]: updatedModule
      };
      
      // Save settings after state update
      setTimeout(() => {
        saveModuleSettings(moduleKey, updatedModule);
      }, 0);
      
      return updatedModules;
    });
  };

  const togglePlatform = (moduleKey, platform) => {
    setModules(prev => {
      const updatedModule = {
        ...prev[moduleKey],
        platforms: {
          ...prev[moduleKey].platforms,
          [platform]: !prev[moduleKey].platforms[platform]
        }
      };
      
      const updatedModules = {
        ...prev,
        [moduleKey]: updatedModule
      };
      
      // Save settings after state update
      setTimeout(() => {
        saveModuleSettings(moduleKey, updatedModule);
      }, 0);
      
      return updatedModules;
    });
  };

  const getEnabledPlatformsCount = (moduleKey) => {
    const module = modules[moduleKey];
    if (!module.enabled) return 0;
    
    // Only count platforms that are in availablePlatforms and enabled
    if (availablePlatforms.length === 0) {
      // Fallback: count all enabled platforms if availablePlatforms not loaded
      return Object.values(module.platforms).filter(Boolean).length;
    }
    
    // Count only platforms that exist in availablePlatforms and are enabled
    let enabledCount = 0;
    availablePlatforms.forEach(platform => {
      const platformCode = platform.code || platform.name?.toLowerCase();
      if (module.platforms[platformCode] === true) {
        enabledCount++;
      }
    });
    
    return enabledCount;
  };

  const getTotalPlatforms = (moduleKey) => {
    // Always use availablePlatforms length if available, otherwise use module platforms
    if (availablePlatforms.length > 0) {
      return availablePlatforms.length;
    }
    // Fallback: count platforms in module
    return Object.keys(modules[moduleKey].platforms).length;
  };

  const getStatusBadge = (moduleKey) => {
    if (!modules[moduleKey].enabled) {
      return <Badge bg="secondary">Disabled</Badge>;
    }
    
    const enabledCount = getEnabledPlatformsCount(moduleKey);
    const total = getTotalPlatforms(moduleKey);
    
    if (enabledCount === total) {
      return <Badge bg="success">All Platforms</Badge>;
    }
    
    if (enabledCount === 0) {
      return <Badge bg="secondary">Disabled</Badge>;
    }
    
    return <Badge bg="info">{enabledCount}/{total} Platforms</Badge>;
  };

  const moduleConfig = [
    {
      key: 'schedulePost',
      title: 'Recurring Schedule',
      description: 'Automatically schedule and publish posts to your social media platforms',
      icon: faMessage,
      color: '#007bff'
    },
    {
      key: 'knackMoments',
      title: 'Knack Moments',
      description: 'Capture and share real-time moments and updates',
      icon: faEdit,
      color: '#ff6b35'
    },
    {
      key: 'knackFeeds',
      title: 'Knack Feeds',
      description: 'Curate and share trending content from your industry',
      icon: faRss,
      color: '#28a745'
    },
    {
      key: 'knackChallenges',
      title: 'Knack Challenges',
      description: 'Create and participate in engaging social media challenges',
      icon: faMedal,
      color: '#ffc107'
    },
    {
      key: 'createPost',
      title: 'Create Post',
      description: 'Manual post creation and publishing to selected platforms',
      icon: faEdit,
      color: '#2e3192'
    }
  ];

  if (loading || availablePlatforms.length === 0) {
    return (
      <SideNavbar>
        <div className="platform-settings-container">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading platforms...</span>
            </div>
          </div>
        </div>
      </SideNavbar>
    );
  }

  return (
    <SideNavbar>
      <div className="platform-settings-container">
        <div className="settings-header">
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faCog} className="me-2" style={{ fontSize: '1.5rem', color: '#2e3192' }} />
            <h2 className="mb-0" style={{ color: '#2e3192', fontWeight: '600' }}>Platform Settings</h2>
          </div>
          <p className="text-muted mb-2">Configure which modules are enabled for each social media platform. Changes are saved automatically.</p>
        </div>

        <div className="modules-grid">
          {moduleConfig.map((module) => {
            const moduleData = modules[module.key];
            const enabledCount = getEnabledPlatformsCount(module.key);

            return (
              <Card key={module.key} className="module-card">
                <Card.Body>
                  <div className="module-header">
                    <div className="d-flex align-items-center">
                      <div 
                        className="module-icon" 
                        style={{ backgroundColor: `${module.color}15`, color: module.color }}
                      >
                        <FontAwesomeIcon icon={module.icon} />
                      </div>
                      <div className="module-title-section">
                        <h5 className="mb-1">
                          {module.title}
                          {savingModule === module.key && (
                            <span className="ms-2">
                              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                            </span>
                          )}
                        </h5>
                        <p className="module-description">{module.description}</p>
                      </div>
                    </div>
                    <div className="module-controls">
                      <Form.Check
                        type="switch"
                        id={`toggle-${module.key}`}
                        checked={moduleData.enabled}
                        onChange={() => toggleModule(module.key)}
                        className="module-toggle"
                        disabled={savingModule === module.key}
                      />
                      {getStatusBadge(module.key)}
                    </div>
                  </div>

                  {moduleData.enabled && (
                    <div className="platform-settings">
                      <div className="platform-cards">
                        {availablePlatforms.length > 0 ? (
                          availablePlatforms.map((platform) => {
                            const platformCode = platform.code || platform.name?.toLowerCase();
                            const platformEnabled = moduleData.platforms[platformCode] === true; // Platform enabled only if explicitly set to true in settings
                            
                            // Platform icons mapping
                            const platformIcons = {
                              linkedin: <FaLinkedin style={{ color: '#0077b5', fontSize: '1.5rem' }} />,
                              twitter: <FaTwitter style={{ color: '#1DA1F2', fontSize: '1.5rem' }} />,
                              facebook: <FaFacebook style={{ color: '#1877F2', fontSize: '1.5rem' }} />,
                              instagram: <FaInstagram style={{ color: '#E4405F', fontSize: '1.5rem' }} />
                            };
                            
                            // Get icon or default
                            const platformIcon = platformIcons[platformCode] || (
                              <div style={{ 
                                width: '1.5rem', 
                                height: '1.5rem', 
                                borderRadius: '4px',
                                backgroundColor: '#6c757d',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}>
                                {platform.name?.charAt(0) || '?'}
                              </div>
                            );

                            return (
                              <div 
                                key={platform.id}
                                className={`platform-card ${platformEnabled ? 'active' : ''}`}
                                onClick={() => togglePlatform(module.key, platformCode)}
                              >
                                <div className="platform-icon">
                                  {platformIcon}
                                </div>
                                <div className="platform-info">
                                  <span className="platform-name">{platform.name}</span>
                                  <Form.Check
                                    type="switch"
                                    id={`${platformCode}-${module.key}`}
                                    checked={platformEnabled}
                                    onChange={() => togglePlatform(module.key, platformCode)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="platform-toggle"
                                    disabled={savingModule === module.key}
                                  />
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted">Loading platforms...</p>
                        )}
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      </div>
    </SideNavbar>
  );
};

export default PlatformSettings;

